/**
 * Chat message model — widget ↔ Laravel WebSocket/API contract (draft).
 */

import type { MessageContentType } from '../design/component-inventory.js';
import type { LinkPreviewData, MessageAttachment } from '../rich-media/types.js';

/** Delivery/read status for outgoing (user) messages */
export type MessageDeliveryStatus =
  | 'sending'   // optimistic UI, upload in progress
  | 'sent'      // accepted by server
  | 'delivered' // delivered to recipient channel (bot/manager)
  | 'read'      // read by recipient
  | 'failed';   // send error; warning shows reason, retry button resends

export type MessageAuthorRole = 'user' | 'bot' | 'manager' | 'system';

/** Client-only code for failed outgoing messages (localized in UI). */
export type MessageSendErrorCode =
  | 'upload'
  | 'too_large'
  | 'unsupported'
  | 'generic';

export type ChatMessage = {
  /** Server message id (optimistic: equals `clientId` until confirmed) */
  id: string;
  /**
   * Stable client-side key for user sends.
   * Kept after server confirm so React does not remount the bubble.
   */
  clientId?: string;
  role: MessageAuthorRole;
  text: string;
  /** ISO 8601 — used for sort + timestamp display */
  createdAt: string;
  /**
   * Only for role === 'user'.
   * Bot/manager messages typically omit or use 'sent'.
   */
  status?: MessageDeliveryStatus;
  /**
   * Client-only reason when `status === 'failed'`.
   * Shown when the user taps the warning icon.
   */
  sendError?: MessageSendErrorCode;
  contentType?: MessageContentType;
  /** Media URL (legacy) — prefer `attachment` when set */
  mediaUrl?: string;
  /** Parsed rich attachment (image / video / audio / file) */
  attachment?: MessageAttachment;
  /** Link preview cards for URLs in text */
  linkPreviews?: LinkPreviewData[];
  /** Client-only typing row (legacy `source_id: 99`) — not from API */
  isTypingPlaceholder?: boolean;
};

/** Milliseconds embedded in optimistic `client-{ts}-…` ids (0 when absent). */
export function clientIdSortTimestamp(message: ChatMessage): number {
  const key = message.clientId ?? message.id;
  const match = /^client-(\d+)-/.exec(key);
  return match ? Number(match[1]) : 0;
}

function compareMessagesByOrder(a: ChatMessage, b: ChatMessage): number {
  const timeDiff =
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  if (timeDiff !== 0) return timeDiff;

  const clientA = clientIdSortTimestamp(a);
  const clientB = clientIdSortTimestamp(b);
  if (clientA !== clientB) return clientA - clientB;

  return String(a.id).localeCompare(String(b.id));
}

/** Sort messages oldest → newest (standard chat order) */
export function sortMessagesByDate(messages: ChatMessage[]): ChatMessage[] {
  return [...messages].sort(compareMessagesByOrder);
}

/** Group consecutive messages by calendar day (for date separators) */
export function groupMessagesByDay(
  messages: ChatMessage[],
): Array<{ dateKey: string; messages: ChatMessage[] }> {
  const sorted = sortMessagesByDate(messages);
  const groups: Array<{ dateKey: string; messages: ChatMessage[] }> = [];

  for (const msg of sorted) {
    const dateKey = msg.createdAt.slice(0, 10);
    const last = groups[groups.length - 1];
    if (last?.dateKey === dateKey) {
      last.messages.push(msg);
    } else {
      groups.push({ dateKey, messages: [msg] });
    }
  }

  return groups;
}
